// frontend/src/components/wallet/VirtualAccountModal.js

import React, { useState } from 'react';
import { X, Shield, AlertCircle, CheckCircle, Copy, ExternalLink, CreditCard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useWalletStore from '../../store/walletStore';

const VirtualAccountModal = ({ onClose, onSuccess }) => {
  const [bvn, setBvn] = useState('');
  const [nin, setNin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [accountDetails, setAccountDetails] = useState(null);
  
  const { createVirtualAccount, isLoading } = useWalletStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one is provided
    if (!bvn.trim() && !nin.trim()) {
      toast.error('Please provide either your BVN or NIN');
      return;
    }

    // Validate BVN format
    if (bvn.trim() && !/^[0-9]{11}$/.test(bvn.trim())) {
      toast.error('BVN must be 11 digits');
      return;
    }

    // Validate NIN format
    if (nin.trim() && !/^[0-9]{11}$/.test(nin.trim())) {
      toast.error('NIN must be 11 digits');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createVirtualAccount(bvn.trim() || null, nin.trim() || null);
      
      if (result.success) {
        setAccountDetails(result.data.account);
        setStep('success');
        onSuccess?.();
      }
    } catch (error) {
      console.error('Virtual account creation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`, {
      duration: 2000,
      position: 'top-right',
    });
  };

  if (step === 'success' && accountDetails) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

          <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-lg">
            <div className="absolute top-0 right-0 pt-3 pr-3 sm:pt-4 sm:pr-4">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-lg shadow-green-500/30 sm:mx-0">
                <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-5 sm:text-left w-full">
                <div className="flex items-center mb-1">
                  <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                    Virtual Account Created!
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-500">
                  Your dedicated bank account is ready. Use these details to receive payments.
                </p>
              </div>
            </div>

            <div className="mt-5 sm:mt-6">
              <div className="bg-[#E8ECF5] rounded-xl p-4 sm:p-5 shadow-neomorph-inset space-y-4">
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Account Number</p>
                  <div className="flex items-center justify-between">
                    <p className="text-lg sm:text-xl font-bold text-gray-800 font-mono">
                      {accountDetails.accountNumber}
                    </p>
                    <button
                      onClick={() => copyToClipboard(accountDetails.accountNumber, 'Account number')}
                      className="bg-white rounded-lg p-2 shadow-neomorph hover:shadow-neomorph-hover transition-all"
                    >
                      <Copy className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Bank Name</p>
                    <p className="text-xs sm:text-sm font-medium text-gray-800">
                      {accountDetails.bankName}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Account Name</p>
                    <p className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                      {accountDetails.accountName}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-3 shadow-neomorph-inset border border-blue-200/50">
                  <div className="flex items-start">
                    <div className="p-1 bg-blue-100 rounded-md shadow-neomorph-inset mr-2 flex-shrink-0">
                      <Shield className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-[10px] sm:text-xs font-medium text-blue-800 mb-0.5">How it works</h4>
                      <p className="text-[9px] sm:text-xs text-blue-700 leading-relaxed">
                        Transfer money to this account and it will be automatically credited to your Fastel wallet.
                        Funds are usually available within 15-30 minutes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row sm:justify-end gap-2">
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-lg">
          <div className="absolute top-0 right-0 pt-3 pr-3 sm:pt-4 sm:pr-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
              disabled={isSubmitting}
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 sm:mx-0">
              <CreditCard className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-5 sm:text-left w-full">
              <div className="flex items-center mb-1">
                <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Get Your Virtual Account
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">
                Receive payments directly to your Fastel wallet via bank transfer
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 sm:mt-6 space-y-4">
            <div className="bg-blue-50 rounded-xl p-3 sm:p-4 shadow-neomorph-inset border border-blue-200/50">
              <div className="flex items-start">
                <div className="p-1 bg-blue-100 rounded-md shadow-neomorph-inset mr-2 sm:mr-3 flex-shrink-0">
                  <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-[10px] sm:text-xs font-medium text-blue-800 mb-0.5 sm:mb-1">
                    Verification Required
                  </h4>
                  <p className="text-[9px] sm:text-xs text-blue-700 leading-relaxed">
                    To create your dedicated bank account, we need your BVN or NIN for verification.
                    Your information is securely encrypted and protected.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="bvn" className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-1.5">
                BVN (11 digits)
              </label>
              <input
                id="bvn"
                type="password"
                value={bvn}
                onChange={(e) => setBvn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="Enter your 11-digit BVN"
                className="block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                disabled={isSubmitting}
                maxLength={11}
              />
              <p className="text-[9px] sm:text-xs text-gray-400 mt-1">
                * At least one of BVN or NIN is required
              </p>
            </div>

            <div>
              <label htmlFor="nin" className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-1.5">
                NIN (11 digits)
              </label>
              <input
                id="nin"
                type="password"
                value={nin}
                onChange={(e) => setNin(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="Enter your 11-digit NIN"
                className="block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                disabled={isSubmitting}
                maxLength={11}
              />
            </div>

            <div className="bg-yellow-50 rounded-xl p-3 shadow-neomorph-inset border border-yellow-200/50">
              <div className="flex items-start">
                <div className="p-1 bg-yellow-100 rounded-md shadow-neomorph-inset mr-2 flex-shrink-0">
                  <AlertCircle className="h-3.5 w-3.5 text-yellow-600" />
                </div>
                <div>
                  <h4 className="text-[10px] sm:text-xs font-medium text-yellow-800 mb-0.5">
                    Important Note
                  </h4>
                  <p className="text-[9px] sm:text-xs text-yellow-700 leading-relaxed">
                    Your BVN/NIN is only used for verification purposes. It will be securely stored 
                    and never shared with third parties. You only need to provide this once.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-3 gap-2 sm:gap-0 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="order-2 sm:order-1 w-full sm:w-auto inline-flex justify-center rounded-lg px-4 py-2.5 bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || (!bvn.trim() && !nin.trim())}
                className="order-1 sm:order-2 w-full sm:w-auto inline-flex justify-center rounded-lg px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Virtual Account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default VirtualAccountModal;